import { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import { Grid, LinearProgress } from '@mui/material';
import { FormField2, IDynamicFormValue } from 'src/lib';
import FilterLoadingSkeleton from 'src/components/skeletons/filtersLoadingSkeleton';
import {
  getNextValues,
  transformDynamicFormValueToFilterPayload
} from 'src/components/filters/utils/filter-form-utils';
import useMatomoFunction from 'src/hooks/use-matomo-function.hook';
import ScenarioManagerTableComponent from './scenario-manager-table';
import { getScenarioManagerTableData } from './scenario-manager-slice';
import ScenarioManagerTopBar from './scenario-manager-top-bar';
import ScenarioManagerFooter from './scenario-manager-footer';
import useFilterFetch from '../common/hooks/use-filter-fetch.hook';
import { Filters2 } from '../common';
import useModulePreRequisites from '../common/hooks/use-module-pre-requisites.hook';
import MODULE_TO_PAGE_MAP from '../home/types/simulation-module-to-page-map';
import {
  fetchFilterFieldsForScenarioManager,
  setGlobalFilterFieldOptions,
  setGlobalFilterValues
} from '../common/features';
import { MatomoEventTrackMapObject } from '../constants/constant';
import { resetSelectedScenario } from '../pricing-simulator/store/pricing-simulator-slice';

export default function ScenarioManager() {
  const appDispatch = useAppDispatch();
  const {
    globalFilterFormFields,
    globalFilterInitialValues,
    globalFilterCombinations,
    globalFilterValue
  } = useAppSelector((state) => state.filters);
  const sourceId = useAppSelector((state) => state.user.source);

  const [selectedRows, setSelectedRows] = useState([]);

  const { isFetchingFilterFields, getFilterFields } = useFilterFetch({
    sourceId,
    filterFieldFetchThunk: (arg) =>
      appDispatch(
        fetchFilterFieldsForScenarioManager({ sourceId: arg.sourceId })
      )
  });

  const { isFetchingPreRequisites } = useModulePreRequisites({
    module: MODULE_TO_PAGE_MAP.PLANNER,
    onPreRequisitesFetched() {
      getFilterFields().then(() => {
        const filterData = { filter_inputs: [] };
        appDispatch(
          getScenarioManagerTableData({
            filterData,
            sourceId
          })
        );
      });
    }
  });

  useEffect(() => {
    appDispatch(resetSelectedScenario());
  }, []);

  const { callFunction } = useMatomoFunction();
  const getPageName = () => {
    const page_title = window.location.href.split('/').at(-1);
    return page_title;
  };
  useEffect(() => {
    callFunction(
      MatomoEventTrackMapObject.track_page_view.event_key,
      getPageName()
    );
  }, []);


  const getScenarioTableData = (
    globalFilterFormValue: IDynamicFormValue,
    filterFormFields?: FormField2[]
  ) => {
    const filters = transformDynamicFormValueToFilterPayload({
      formValue: globalFilterFormValue,
      formFields: filterFormFields,
      combinations: globalFilterCombinations
    });
    const payload = filters;
    return appDispatch(
      getScenarioManagerTableData({
        filterData: { filter_inputs: payload },
        sourceId
      })
    );
  };

  /**
   * Handler for apply button click
   */
  const handleGlobalFilterApply = (
    filterValue: IDynamicFormValue,
    filterFormFields?: FormField2[]
  ) => getScenarioTableData(filterValue, filterFormFields);

  const handleGlobalFilterValueChange = ({
    fieldNameToOptionsMap
  }: ReturnType<typeof getNextValues>) => {
    appDispatch(setGlobalFilterFieldOptions(fieldNameToOptionsMap));
  };

  /**
   * Handler for planner reset
   */
  const handlePlannerFiltersReset = () => {
    const filterData = { filter_inputs: [] };
    appDispatch(getScenarioManagerTableData({ filterData, sourceId }));
  };

  if (isFetchingPreRequisites) {
    return (
      <Grid container>
        <Grid item xs={12}>
          <LinearProgress />
        </Grid>
      </Grid>
    );
  }



  return (
    <Grid sx={{ padding: (theme) => theme.spacing(1, 2) }}>
      <Grid item xs={12}>
        {isFetchingFilterFields ? (
          FilterLoadingSkeleton(4)
        ) : (
          <Filters2
            formValueInState={globalFilterValue}
            formFields={globalFilterFormFields}
            initialValues={globalFilterInitialValues}
            onChange={(value) => {
              handleGlobalFilterValueChange(value);
            }}
            combinations={globalFilterCombinations}
            onReset={handlePlannerFiltersReset}
            onApplyFilters={(value) => {
              appDispatch(setGlobalFilterValues(value));
              handleGlobalFilterApply(value, globalFilterFormFields);
            }}
          />
        )}
      </Grid>
      <Grid container sx={{ mt: 1 }}>
        <Grid item xs={12}>
          <ScenarioManagerTopBar isNewScenarioBtnPresent />
        </Grid>
        <Grid item xs={12}>
          <ScenarioManagerTableComponent
            onRowSelectionChange={setSelectedRows}
          />
        </Grid>
        <Grid item xs={12}>
          <ScenarioManagerFooter selectedScenarios={selectedRows} />
        </Grid>
      </Grid>
    </Grid>
  );
}
